// Module: useClassrooms
// -----------------------------------------------------------------
// Receives:  the signed-in user and acting teacher (useAuthStore)
// Produces:  the viewer's classrooms from GET /classrooms, shared app-wide
// Owns:      one in-memory list per viewer and the single in-flight request,
//            so the workspace shell and a classrooms page never fetch twice
// Does NOT:  persist across reloads (an account switch reloads the page),
//            or create, edit or delete classrooms
// -----------------------------------------------------------------
import { useEffect } from "react";
import { create } from "zustand";
import api from "@/utils/axios-utils";
import { useAuthStore } from "@/store/useAuthStore";
import type { Classroom } from "@/types/tutor";

interface ClassroomsCache {
  /** `${userId}:${actingTeacherId}` -- the list answers for this viewer only. */
  viewerKey: string | null;
  classrooms: Classroom[] | null;
  isLoading: boolean;
  error: unknown;
}

const emptyCache: ClassroomsCache = {
  viewerKey: null,
  classrooms: null,
  isLoading: false,
  error: null,
};

const useClassroomsCache = create<ClassroomsCache>(() => emptyCache);

let inFlight: { viewerKey: string; request: Promise<void> } | null = null;

/**
 * Input:   viewerKey -- whose list to fetch.
 * Output:  the request already running for that viewer, or a new one.
 * Mutates: the cache; a different viewer's list is dropped before fetching.
 */
function loadClassrooms(viewerKey: string): Promise<void> {
  if (inFlight?.viewerKey === viewerKey) return inFlight.request;
  const cache = useClassroomsCache.getState();
  useClassroomsCache.setState({
    viewerKey,
    classrooms: cache.viewerKey === viewerKey ? cache.classrooms : null,
    isLoading: true,
    error: null,
  });
  const isStillViewer = () =>
    useClassroomsCache.getState().viewerKey === viewerKey;
  const request = api
    .get<Classroom[]>("/classrooms")
    .then(
      ({ data }) => {
        if (isStillViewer()) {
          useClassroomsCache.setState({ classrooms: data ?? [], error: null });
        }
      },
      (error: unknown) => {
        console.error("Error fetching classrooms:", error);
        if (isStillViewer()) useClassroomsCache.setState({ error });
      },
    )
    .finally(() => {
      if (inFlight?.request === request) inFlight = null;
      if (isStillViewer()) useClassroomsCache.setState({ isLoading: false });
    });
  inFlight = { viewerKey, request };
  return request;
}

/** Test seam: forget every viewer's list and any request in flight. */
export function resetClassroomsCache() {
  inFlight = null;
  useClassroomsCache.setState(emptyCache);
}

/**
 * Input:   isEnabled -- false skips fetching (the caller has no use for it).
 *          shouldRevalidate -- refetch on mount even when a list is cached;
 *          the cached list stays on screen until the fresh one lands.
 * Output:  classrooms -- null until this viewer's list is first known.
 *          error -- the last fetch's failure, cleared by the next attempt.
 *          reload -- start a fetch now (joins one already running).
 */
export function useClassrooms({
  isEnabled = true,
  shouldRevalidate = false,
}: { isEnabled?: boolean; shouldRevalidate?: boolean } = {}) {
  const userId = useAuthStore((s) => s.user?.id);
  const actingTeacherId = useAuthStore((s) => s.actingTeacherId);
  const viewerKey =
    userId == null ? null : `${userId}:${actingTeacherId ?? ""}`;
  const cache = useClassroomsCache();
  const isCurrentViewer = viewerKey !== null && cache.viewerKey === viewerKey;

  useEffect(() => {
    if (!isEnabled || viewerKey === null) return;
    const current = useClassroomsCache.getState();
    const hasList =
      current.viewerKey === viewerKey && current.classrooms !== null;
    if (shouldRevalidate || !hasList) void loadClassrooms(viewerKey);
  }, [isEnabled, viewerKey, shouldRevalidate]);

  return {
    classrooms: isCurrentViewer ? cache.classrooms : null,
    isLoading: isCurrentViewer ? cache.isLoading : isEnabled,
    error: isCurrentViewer ? cache.error : null,
    reload: () => {
      if (viewerKey !== null) void loadClassrooms(viewerKey);
    },
  };
}
